import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import TableWithDrag from '../../common/TableWithDrag'
import InputHideable from '../../common/InputHideable'
import { ItemTypes, tableColor, tableHeaderSecondaryBgColor, variableHandlingType, variablesIcons } from '../../../constants/constants'
import { convertContentToRawText } from '../../RichEditor/utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../store/store'
import { faCirclePlus } from '@fortawesome/pro-light-svg-icons'
import FormCreateItemButton from './FormCreateItemButton'
import { sortAndFilterVariablesGroupedByContainer } from '../../../utils/variableOrderArray'
import { scrollToItemInVariableEditor } from '../FormViewer/FormViewer'
import { hasVariableBoxError } from '../../../utils/boxes'
import { faTrashXmark } from '@fortawesome/pro-duotone-svg-icons'
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro'
import { Popconfirm } from 'antd';
import Tooltip from '../../common/Tooltip';
import { Editor } from 'draft-js'
import { searchCaseDiatricsInsensitive } from '../../../utils/general'
import { useQueryDocumentSections } from '../../../hooks'
import { shouldHideRowInForm } from '../../../utils/variables'
import { addressPartKeys, withAddressParts } from '../../../utils/address'
import { hasOptionalEmptyValue } from '../../../utils/formMissingValues'

const formatVariables = ({ variablesGroupedByContainer, variableOrderArray, documentType, sectionList, displayedVariables }) => {
  const sortedAndFiltered = sortAndFilterVariablesGroupedByContainer({ variablesGroupedByContainer, variableOrderArray, filterHiddenBoxes: documentType !== 'template' }).filter((vInfos) => {
    if (documentType !== 'template' &&
      shouldHideRowInForm({ vInfos, sectionList })) return false
    return true;
  });

  // Logique de déduplication basique pour le mode proposal
  if (documentType !== 'template' && displayedVariables) {
    const seenVariableIds = new Set();
    const seenGroupIds = new Set(); // Pour tracker les groupements déjà affichés

    return sortedAndFiltered.map((vInfos) => {
      if (vInfos.type === variableHandlingType.FORM) {
        // Pour les variables FORM, déduplication par ID
        const filtered = vInfos.variablesBoxes.filter((b) => {
          if (displayedVariables[b.id] || seenVariableIds.has(b.id)) {
            return false; // Déjà affichée
          }
          seenVariableIds.add(b.id);
          return true;
        });
        return filtered.map((b) => ({ ...b, canDrag: !!b.onlyForm, containerId: vInfos.containerId }));
      }

      if (vInfos.variableGroupBox) {
        // Pour les groupements, vérifier si le groupement a déjà été affiché
        const groupId = vInfos.variableGroupBox.id;

        // Si ce groupement a déjà été vu dans cette passe, ne pas l'afficher à nouveau
        if (seenGroupIds.has(groupId)) {
          return []; // Groupement déjà affiché
        }

        // Vérifier si toutes les variables du groupement sont déjà dans displayedVariables
        const allVariablesDisplayed = vInfos.variablesBoxes.every((b) => displayedVariables[b.id]);
        if (allVariablesDisplayed) {
          return []; // Toutes les variables sont déjà affichées, ne pas afficher le groupement
        }

        // Marquer ce groupement comme vu
        seenGroupIds.add(groupId);
        // Inclure toutes les variables actuelles du groupement pour le calcul des erreurs
        const allVariableIds = vInfos.variablesBoxes.map((b) => b.id);
        return [{ ...vInfos.variableGroupBox, containerId: vInfos.containerId, allVariableIds }];
      }

      // Pour les variables individuelles, déduplication par ID
      const filtered = vInfos.variablesBoxes.filter((b) => {
        if (displayedVariables[b.id] || seenVariableIds.has(b.id)) {
          return false; // Déjà affichée
        }
        seenVariableIds.add(b.id);
        return true;
      });
      return filtered.map((b) => ({ ...b, containerId: vInfos.containerId }));
    }).flat().map(((v, index) => ({ ...v, key: `${v.id}-${index}` })));
  }

  // Mode template : comportement original (pas de déduplication)
  return sortedAndFiltered.map((vInfos) => {
    if (vInfos.type === variableHandlingType.FORM) return vInfos.variablesBoxes.map((b) => ({ ...b, canDrag: !!b.onlyForm, containerId: vInfos.containerId }))
    if (vInfos.variableGroupBox) return { ...vInfos.variableGroupBox, containerId: vInfos.containerId }
    return vInfos.variablesBoxes.map((b) => ({ ...b, containerId: vInfos.containerId }))
  }).flat().map(((v, index) => ({ ...v, key: `${v.id}-${index}` })))
}

const FormItemsArray = ({ variablesGroupedByContainer, setCreateItemType }) => {
  const documentType = useStore(({ currentDocument }) => currentDocument.type)
  const documentId = useStore(({ currentDocument, currentProposal }) =>
    currentDocument.type === 'template'
      ? currentDocument.id
      : currentProposal.id
  );
  const { data: sectionList } = useQueryDocumentSections({
    documentType,
    documentId,
  });
  const variableOrderArray = useStore(({ variableOrderArray }) => variableOrderArray)
  const variables = useStore(({ variables }) => variables)
  const boxVariableSelectedId = useStore(({ boxVariableSelectedId }) => boxVariableSelectedId)
  const handleBoxVariableSelectedId = useStore(({ handleBoxVariableSelectedId }) => handleBoxVariableSelectedId)
  const itemsChangedByTriggers = useStore(({ itemsChangedByTriggers }) => itemsChangedByTriggers)
  const deleteItemChangedByTriggers = useStore(({ deleteItemChangedByTriggers }) => deleteItemChangedByTriggers)
  const areSectionsActivating = useStore(({ areSectionsActivating }) => areSectionsActivating)
  const [searchValue, setSearchValue] = useState("");
  const [menuStatus, setMenuStatus] = useState({ isOpen: false, from: null })
  const displayedVariables = useStore(({ displayedVariables }) => displayedVariables)
  const showAllVariables = useStore(({ showAllVariables }) => showAllVariables)
  const [dataSource, setDataSource] = useState(formatVariables({ variablesGroupedByContainer, variableOrderArray, documentType, sectionList, displayedVariables }));
  const reorderVariables = useStore(({ reorderVariables }) => reorderVariables)
  const removeBox = useStore(({ removeBox }) => removeBox)
  const { t } = useTranslation()

  // Mémoriser les erreurs pour éviter de les recalculer à chaque rendu
  // Utiliser un ref pour éviter les problèmes de dépendances
  const errorCacheRef = useRef(new Map());

  // Fonction pour créer une clé de cache qui inclut les valeurs des variables/boxes
  // Utiliser useCallback pour éviter les problèmes de dépendances
  const getCacheKey = useCallback((record, recordToCheck) => {
    // Pour les groupements, inclure les valeurs de toutes les variables
    if (record.type === ItemTypes.VARIABLE_GROUP) {
      const variableIdsForCache = recordToCheck.variableIds || [];
      if (variableIdsForCache.length > 0) {
        const valuesHash = variableIdsForCache
          .map((vId) => {
            const v = variables[vId];
            return v ? `${vId}:${v.value || ''}:${v.notUsed || false}:${v.optional || false}` : `${vId}:null`;
          })
          .sort()
          .join('|');
        return `${record.id}-${record.type}-${valuesHash}`;
      }
      return `${record.id}-${record.type}-${JSON.stringify(variableIdsForCache.sort())}`;
    }

    // Pour les variables individuelles, le record est une box
    // Inclure les propriétés pertinentes de la box dans la clé selon le type
    const box = recordToCheck || record;
    let valueHash = '';

    if (box.type === ItemTypes.TEXT_VARIABLE && box.content?.editorState) {
      // Pour TEXT_VARIABLE, utiliser le texte du content ET value_required (logique métier)
      try {
        const text = box.content.editorState.getCurrentContent().getPlainText();
        valueHash = `${text}-${box.value_required || false}`;
      } catch (e) {
        valueHash = `-${box.value_required || false}`;
      }
    } else if ((box.type === ItemTypes.CHECKBOXS_VARIABLE || box.type === ItemTypes.QUESTION) && box.checkboxs) {
      // Pour CHECKBOXS_VARIABLE et QUESTION, utiliser le nombre de cases cochées ET minNumberOfAnwser (logique métier)
      valueHash = `${box.checkboxs.filter((c) => c.checked).length}-${box.minNumberOfAnwser || 0}`;
    } else if (box.type === ItemTypes.SELECTOR_VARIABLE && box.options) {
      // Pour SELECTOR_VARIABLE, utiliser le nombre d'options sélectionnées ET minNumberOfAnwser (logique métier)
      valueHash = `${box.options.filter((c) => c.selected).length}-${box.minNumberOfAnwser || 0}`;
    } else if (box.type === ItemTypes.SECTION_VARIABLE) {
      // Pour SECTION_VARIABLE, utiliser answer (toujours obligatoire)
      valueHash = box.answer ? '1' : '0';
    } else if (box.type === ItemTypes.IMG_VARIABLE && box.content?.src) {
      // Pour IMG_VARIABLE, utiliser src
      valueHash = box.content.src || '';
    } else if (box.type === ItemTypes.CUSTOM_TEXT_VARIABLE) {
      // Pour CUSTOM_TEXT_VARIABLE, utiliser value et optional (réglé dans le modèle)
      valueHash = `${box.value || ''}-${variables[box.id]?.optional || false}`;
    } else if (box.type === ItemTypes.TABLE_VARIABLE && box.content?.data) {
      // Pour TABLE_VARIABLE : inclure structure (nb lignes + clés) pour que add/suppression de ligne invalide le cache
      try {
        const rowKeys = box.content.data.map((row) => row.key).join(',');
        const cellsHash = box.content.data
          .map((row) => {
            return Object.keys(row)
              .filter((key) => key !== 'key' && !key.startsWith('textAlign_'))
              .map((key) => {
                const val = row[key];
                if (typeof val === 'string') return val.substring(0, 50); // Limiter la longueur
                return String(val || '');
              })
              .join('|');
          })
          .join('||');
        valueHash = `${box.content.data.length}-${rowKeys}-${cellsHash.substring(0, 200)}`;
      } catch (e) {
        valueHash = '';
      }
    }

    // optional (réglé dans le modèle) change l'erreur comme l'icône « i »
    return `${box.id}-${box.type}-${valueHash}-${box.optional || false}`;
  }, [variables]);

  useEffect(() => {
    // Invalider le cache à chaque recalcul pour que les changements de structure (ex. lignes tableau)
    // soient bien pris en compte (suppression/ajout de ligne tableau variable).
    errorCacheRef.current.clear();

    let newData = formatVariables({ variablesGroupedByContainer, variableOrderArray, documentType, sectionList, displayedVariables })

    // Appliquer le filtre pour n'afficher que les données avec erreurs (en mode proposal uniquement)
    if (documentType !== 'template' && !showAllVariables) {
      newData = newData.filter((record) => {
        // Pour les groupements, utiliser allVariableIds (toutes les variables actuelles) si disponible
        // Sinon utiliser variableIds (pour compatibilité)
        let recordToCheck = record;
        if (record.type === ItemTypes.VARIABLE_GROUP) {
          const variableIdsToUse = record.allVariableIds || record.variableIds || [];
          recordToCheck = {
            ...record,
            variableIds: variableIdsToUse
          };
        }
        // Utiliser le cache pour éviter de recalculer les erreurs (recalculé à chaque run car cache vidé ci-dessus)
        const cacheKey = getCacheKey(record, recordToCheck);
        let error = errorCacheRef.current.get(cacheKey);
        if (!error) {
          error = hasVariableBoxError(recordToCheck, variables);
          errorCacheRef.current.set(cacheKey, error);
        }
        // Garder les enregistrements avec erreurs, et les optionnelles restées
        // vides : signalées par leur icône « i », sans compter comme manquantes.
        return error.error || hasOptionalEmptyValue(recordToCheck, variables);
      });
    }

    if (searchValue) {
      setDataSource(newData
        .filter((record) => {
          const isNameSearched = searchCaseDiatricsInsensitive(searchValue, typeof record.name === "string" ? record.name : convertContentToRawText(record.name))
          if (record.type !== ItemTypes.VARIABLE_GROUP) return isNameSearched
          return isNameSearched || record.variableIds
            .flatMap((id) => (variables[id]?.derived ? [id, ...addressPartKeys(id)] : [id]))
            .some((id) => variables[id] && searchCaseDiatricsInsensitive(searchValue, variables[id].name))
        })
        .map((record) => ({
          ...record,
          canDrag: false,
          dragIconTitle: t('unmovable-search-active')
        })))
    } else {
      setDataSource(documentType === 'template' ? newData.map((d, idx) => ({
        ...d,
        onAdd: (e) => {
          const { top, left, width } = e.target.getBoundingClientRect()
          setMenuStatus({ isOpen: true, from: { top, left, width, id: d.id } })
        },
        onAddBottom: idx === newData.length - 1 ? (e) => {
          const { top, left, width } = e.target.getBoundingClientRect()
          setMenuStatus({ isOpen: true, from: { top, left, width, id: d.id, isAddedFromBottom: true } })
        } : undefined
      }))
        : newData)
    }
  }, [searchValue, variablesGroupedByContainer, t, variableOrderArray, sectionList, documentType, variables, displayedVariables, showAllVariables, getCacheKey])

  const onDrop = useCallback(() => {
    reorderVariables(dataSource)
  }, [dataSource, reorderVariables])

  const ref = useRef()

  const getPositions = useCallback(() => {
    if (menuStatus.from === null) return { top: null, left: null }
    if (menuStatus.from !== 'header') {
      const { left, top, width } = menuStatus.from
      return { left: left + width / 2 - 73, top: top + 16 + 61 > window.innerHeight ? window.innerHeight - 61 : top + 16 }
    }
    if (!ref.current) return { top: null, left: null }
    const { left, top, width } = ref.current.getBoundingClientRect()
    return { left: left + width, top }
  }, [menuStatus.from])

  const columns = useMemo(() => {
    const base = [
      {
        title: <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16
        }}>
          {t('name')}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flex: 1
          }}>
            <InputHideable
              setValue={setSearchValue}
              value={searchValue}
            />
            {documentType === 'template' && <FontAwesomeIcon
              ref={ref}
              onClick={() => setMenuStatus({ isOpen: true, from: "header" })}
              style={{
                height: 16,
                color: '#0b2239',
                cursor: 'pointer'
              }} icon={faCirclePlus} />}
          </div>
        </div>,
        dataIndex: 'name',
        render: (name, record) => <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}>
          <FontAwesomeIcon icon={variablesIcons[record.type]} />
          {typeof name === "string" ? name : <Editor
            readOnly
            editorState={name} />}
          {documentType !== 'template' && (() => {
            let recordToCheck = record;
            // Pour les groupements, utiliser allVariableIds si disponible (toutes les variables actuelles)
            if (record.type === ItemTypes.VARIABLE_GROUP && record.allVariableIds) {
              recordToCheck = {
                ...recordToCheck,
                variableIds: record.allVariableIds
              };
            }
            // Utiliser le cache pour éviter de recalculer les erreurs
            const cacheKey = getCacheKey(record, recordToCheck);
            let error = errorCacheRef.current.get(cacheKey);
            if (!error) {
              error = hasVariableBoxError(recordToCheck, variables);
              errorCacheRef.current.set(cacheKey, error);
            }
            if (error.error) {
              return (
                <Tooltip title={error.message}>
                  <FontAwesomeIcon
                    icon={icon({ name: 'exclamation-triangle', style: 'duotone' })}
                    style={{
                      fontSize: 18,
                      marginLeft: 'auto',
                      "--fa-primary-color": "black",
                      "--fa-secondary-color": "#faad14",
                      "--fa-secondary-opacity": 1
                    }} />
                </Tooltip>
              );
            }
            if (hasOptionalEmptyValue(recordToCheck, variables)) {
              return (
                <Tooltip title={t('optional-value-empty')}>
                  <FontAwesomeIcon
                    icon={icon({ name: 'circle-info', style: 'duotone' })}
                    style={{
                      fontSize: 16,
                      marginLeft: 'auto',
                      "--fa-primary-color": "#596579",
                      "--fa-secondary-color": "#d9d9d9",
                      "--fa-secondary-opacity": 1
                    }} />
                </Tooltip>
              );
            }
            return null;
          })()}
        </div >
        ,
        onHeaderCell: () => ({
          style: {
            backgroundColor: tableHeaderSecondaryBgColor,
            color: tableColor
          },
        }),
        onCell: (record) => ({
          onClick: () => {
            // Passer shouldScroll=true pour indiquer qu'on doit scroller dans FormViewer
            handleBoxVariableSelectedId(record.id, true)
            if (documentType === 'template')
              setCreateItemType()
            const formItemsForScroll = sortAndFilterVariablesGroupedByContainer({ variablesGroupedByContainer, variableOrderArray, filterHiddenBoxes: documentType !== 'template' }).filter((vInfos) => {
              if (shouldHideRowInForm({ vInfos, sectionList })) return false
              return true;
            });
            scrollToItemInVariableEditor(record.id, formItemsForScroll)
          },
          style: {
            cursor: 'pointer'
          }
        })

      },
    ]
    if (documentType === 'template') return [
      ...base,
      {
        width: 25,
        dataIndex: 'delete',
        render: (_, record) =>
          <Popconfirm
            title="Supprimer la question ?"
            cancelText='Annuler'
            placement='bottom'
            onConfirm={() => removeBox(record.id)}
            disabled={record.from_linked_section}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: "100%",
              cursor: record.from_linked_section ? 'not-allowed' : 'pointer'
            }}>
              {record.onlyForm && <FontAwesomeIcon color={record.from_linked_section ? '#b2b2b2' : 'red'} icon={faTrashXmark} />}
            </div >
          </Popconfirm>
        ,
        onHeaderCell: () => ({
          style: {
            backgroundColor: tableHeaderSecondaryBgColor,
            color: tableColor
          },
        }),
      },
    ]
    return base
  }, [documentType, handleBoxVariableSelectedId, removeBox, searchValue, sectionList, setCreateItemType, t, variableOrderArray, variables, variablesGroupedByContainer, getCacheKey]);

  // Ligne à surligner : la box elle-même pour une question du formulaire, sinon
  // la ligne de son conteneur (variable ou groupe), dont record.id n'est pas
  // l'id de la box sélectionnée — on compare donc sur containerId.
  const selectedRow = useMemo(() => {
    if (!boxVariableSelectedId) return
    const v = variablesGroupedByContainer.find((v) => v.containerId === boxVariableSelectedId || withAddressParts(v.variablesBoxes).some(({ id }) => id === boxVariableSelectedId))
    if (!v) return
    if (v.type === variableHandlingType.FORM) return { id: boxVariableSelectedId }
    return { containerId: v.containerId }
  }, [boxVariableSelectedId, variablesGroupedByContainer])

  const rowClassNameforItemsChanged = useCallback((record) => {
    let base = "form-item-array-row";
    if (selectedRow && (selectedRow.id ? record.id === selectedRow.id : record.containerId === selectedRow.containerId)) {
      base += " table-with-drag-row-selected";
    }
    if (itemsChangedByTriggers.some(({ key }) => key === record.id) && !areSectionsActivating) {
      base += ` form-item-array-row-changed record-id-${record.id}`;
    }
    return base;
  }, [itemsChangedByTriggers, areSectionsActivating, selectedRow])

  return (
    <div
      onAnimationEnd={(e) => {
        const element = e.target;
        const className = element.className;
        const recordIdMatch = className.match(/record-id-([^\s]+)/);
        if (recordIdMatch) {
          const recordId = recordIdMatch[1];
          deleteItemChangedByTriggers(recordId)
        }
      }}
    >
      <FormCreateItemButton menuStatus={menuStatus} setMenuStatus={setMenuStatus} getPositions={getPositions} setCreateItemType={setCreateItemType} />
      <TableWithDrag
        columns={columns}
        dataSource={dataSource}
        setDataSource={setDataSource}
        backgroundColor={tableHeaderSecondaryBgColor}
        color={tableColor}
        onDrop={onDrop}
        scroll={{ y: "calc(100vh - 54px - 60px - 16px - 47.5px" }}
        withDragger={documentType === 'template'}
        rowKey="key"
        rowClassName={rowClassNameforItemsChanged}
        locale={{
          emptyText: (
            <div style={{ padding: '8px 0', fontSize: '14px' }}>
              Aucune valeur
            </div>
          )
        }}
      />
    </div>
  )
}

export default FormItemsArray
